import { useDebounce } from '@/hooks/use-debounce';
import { useGetAvailableTemplatesForOrgPaginated } from '@/modules/templates/hooks/use-templates';
import type { Template } from '@/modules/templates/types/templates.types';
import { useState } from 'react';

export const PUBLIC_TEMPLATES_PAGE_SIZE = 10;
export const PUBLIC_TEMPLATES_QUERY_KEY = ['availableTemplatesForOrgPaginated'];

export interface PublicTemplatesController {
  searchInput: string;
  onSearchChange: (value: string) => void;
  category: string | null;
  onCategoryChange: (category: string | null) => void;
  page: number;
  setPage: (page: number) => void;
  templates: Template[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
}

/**
 * Owns the Public Templates tab state (search, category, pagination) and its
 * paginated query. `publicOnly` excludes the org's own available templates so
 * only pure public templates are listed.
 */
export const usePublicTemplates = (orgId?: string | null): PublicTemplatesController => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [category, setCategory] = useState<string | null>(null);

  const { data, isLoading } = useGetAvailableTemplatesForOrgPaginated({
    orgId,
    pageNo: page,
    pageSize: PUBLIC_TEMPLATES_PAGE_SIZE,
    search,
    category,
    publicOnly: true,
  });

  return {
    searchInput,
    onSearchChange: (value) => {
      setSearchInput(value);
      setPage(1);
    },
    category,
    onCategoryChange: (value) => {
      setCategory(value);
      setPage(1);
    },
    page,
    setPage,
    templates: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 1,
    isLoading,
  };
};
